import React, { useState, useEffect, useContext } from 'react';
import Faq from '@/components/Faq/FaqV2/Faq';
import { Box } from '@/components/Box/Box';
import { getUrlFromEnv } from '@/utils/getUrlFromEnvUtil';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import Layout from '@/components/layout';
import MobileAppInstallPrompts from '@latitude/mobile-app-install-prompt/MobileAppInstallPrompt';
import PageData from '@/data/pages/home-renovation-loans.json';
import PLData from '@/data/pages/personal-loan.json';
import { redirectUrl } from '@/components/EstimateRateWidget/utils';
import StickyNavigation from '@/components/StickyNavigation/StickyNavigationV2';
import FeaturesSlider from '@/components/FeaturesSlider/FeaturesSlideV2';
import { COLOR, SOFT_QUOTE_LOANS_AU_URL } from '../utils/constants';
import footerData from '@/data/json/footer.json';
import { PageContext } from '@/context/PageContext';
import Section from '@latitude/section-rebrand';
import { PL_APPLY_CTA_TRACK_ID } from '@/utils/constants';
import ProductComparisonSection from '@/components/PersonalLoanPageContent/ProductComparisionSectionRebrand';
import PersonalLoanCalculatorV2 from '@/components/PersonalLoanCalculatorRebrandV2/PersonalLoanCalculator';
import CardCollection from '@/components/lab-components/CardCollection/CardCollectionV2';
import ProofPointCollection from '@/components/lab-components/ProofPointCollection/ProofPointCollectionV2';
import Typography from '@latitude/typography';
import articleTransformer from '@/utils/articleTransformer';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import PromoBanner from '@/components/lab-components/PromoBanner/PromoBannerV2';

const titles = [
  'How Do Colours Affect Your Home?',
  '5 Things You Should Look for in a Builder',
  'How Much Money Should I Spend on a Renovation?'
];

const featureSliderJson = [
  {
    heading: 'Why choose a Latitude loan?',
    featureCards: PageData.content.whyChooseLatitude
  }
];

const CTA_BUTTON_LABEL = 'Start here';
const HomeRenoPage = ({ data, location }) => {
  const applyUrl = `${getUrlFromEnv(
    'funnel-start-loans'
  )}${SOFT_QUOTE_LOANS_AU_URL}`;

  // soft quote
  const [softQuoteHref, setSoftQuoteHref] = useState();

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};

  const articlesData = articleTransformer(titles);
  useEffect(() => {
    sessionStorage.setItem('purpose', 'home-improvements'); // reset session val used in `EstimateRateWidgetSection`
    setSoftQuoteHref(redirectUrl('au', true));
  }, []);

  return (
    <Layout
      location={location}
      canonical={
        location.canonical ||
        'https://www.latitudefinancial.com.au/home-renovation-loans/'
      }
      metaDesc={PageData.metaDesc}
      title={PageData.title}
      customFooter={footerData}
    >
      <main className="navigation-spacer">
        <Box backgroundColor={COLOR.GREY6}>
          <MobileAppInstallPrompts />

          <HeroV2
            {...(state?.heroBannerData?.[0] ||
              PLData?.contentful?.heroBannerDataHome?.[0])}
          />
          <StickyNavigation
            items={PLData?.contentful?.inPageNavData.items}
            phoneNumber={PLData.content.phoneNumber}
            ctaHref={applyUrl}
            ctaText={CTA_BUTTON_LABEL}
            trackEventData={{
              category: 'cta',
              action: 'quote-link',
              location: 'sticky'
            }}
            trackId="sticky-get-started"
            offsetElem="[data-sticky-navigation-offset]"
            {...state?.inPageNavData?.[0]}
          />
          <FeaturesSlider
            key={
              (
                state?.featureSliderData?.[0] ||
                PLData?.contentful?.featureSliderData[0]
              ).heading
            }
            heading={
              (
                state?.featureSliderData?.[0] ||
                PLData?.contentful?.featureSliderData[0]
              ).heading
            }
            id="why-us"
            className="why-choose"
            backgroundColor={
              state?.featureSliderData?.[0]?.backgroundColor?.value ||
              REBRAND_COLOR.WHITE
            }
            subheading={
              (
                state?.featureSliderData?.[0] ||
                PLData?.contentful?.featureSliderData[0]
              ).description
            }
            data={
              (
                state?.featureSliderData?.[0] ||
                PLData?.contentful?.featureSliderData[0]
              ).featureCards
            }
          />
          <section
            id="repayment-calculator"
            css={`
              padding: 0px !important;
            `}
          >
            <div
              id="repayment-calculator"
              css={`
                background-color: ${REBRAND_COLOR.WHITE};
                padding-top: 32px;
                padding-bottom: 32px;

                && .typography-base__sh1 {
                  margin-bottom: 30px !important;
                }
              `}
            >
              <Section>
                <Typography.SH1 color={REBRAND_COLOR.BLACK} semibold>
                  Get an estimate of your repayments
                </Typography.SH1>

                <PersonalLoanCalculatorV2
                  loans1
                  ctaButtonLabel="Get your personalised rate"
                  applyCTATrackId={PL_APPLY_CTA_TRACK_ID}
                />
              </Section>
            </div>
          </section>
          <PromoBanner
            {...(state?.promoBannerData?.[0] ||
              PLData?.contentful?.promoBannerData?.[0])}
          />

          <ProductComparisonSection
            id="compare-products"
            {...state?.productComparisonData?.[0]}
          />

          <ProofPointCollection
            id="calculators-and-tools"
            data={
              state?.proofPointCollectionData?.[0] ||
              PLData?.contentful?.proofPointCollectionData?.[0]
            }
          />

          <CardCollection
            id="applying-is-simple"
            data={
              state?.cardCollectionData?.[0] ||
              PLData?.contentful?.cardCollectionData?.[0]
            }
          />

          <CardCollection
            category="text-link"
            data={state?.cardCollectionData?.[1] || articlesData}
          />

          {state?.faqData?.[0] && <Faq {...state?.faqData?.[0]} />}
          <ImportantInformation
            data={require('@/data/json/disclaimer/personal-loan1.json')}
            css={`
              background-color: ${REBRAND_COLOR.STONE};
              overflow-x: hidden;
              .row {
                @media (min-width: 1600px) {
                  padding-left: ${PADDING.P32};
                  padding-right: ${PADDING.P32};
                }
              }
            `}
            {...{
              ...state?.importantInfoData?.[0],
              sectionOneColummClasses: 'col-12 col-lg-6',
              sectionTwoColummClasses: 'col-12 col-lg-6'
            }}
          />
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'http://schema.org',
                '@type': 'LoanOrCredit',
                name: PLData.content.loanDetails.personal.name,
                loanTerm: PLData.content.loanDetails.personal.loanTerm,
                annualPercentageRate:
                  PLData.content.loanDetails.personal.annualPercentageRate,
                amount: PLData.content.loanDetails.personal.amount
              })
            }}
          />
        </Box>
      </main>
    </Layout>
  );
};

export default HomeRenoPage;
